import { Box, Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import {
    VendorActivationStatus,
    getPropertyVendorsDisplayMap,
} from 'entities/Vendors';
import { SupplierLogo } from 'shared/ui/SupplierLogo';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { SupplierStatusChip } from './SupplierStatusChip';

import { useSupplierActions } from '../lib/hooks/useSupplierActions';
import { sortSupplierProposals } from '../lib/sort/sortSupplierProposals';

interface SuppliersStatusListProps {
    proposals: Proposal[];
}

export const SuppliersStatusList = ({
    proposals,
}: SuppliersStatusListProps) => {
    const { t } = useTranslation();
    const vendorsDisplayMap = useSelector(getPropertyVendorsDisplayMap);
    const { handleEnableProductOrdering, getProposalStatus, vendorsMap } =
        useSupplierActions();

    const sortedProposals = useMemo(
        () => sortSupplierProposals(proposals ?? [], vendorsMap),
        [proposals, vendorsMap],
    );

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('Suppliers')}
            </Typography>

            {sortedProposals.map((proposal) => {
                const { activationStatus, showEnableButton, vendor } =
                    getProposalStatus(proposal);

                let displayVendor =
                    vendorsDisplayMap?.[proposal.seller.uid] || vendor;

                return (
                    <Box
                        key={proposal.uid}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 0',
                            borderBottom: '1px solid #D9D9D9',
                            gap: '20px',
                            '&:last-of-type': {
                                borderBottom: 'none',
                                paddingBottom: 0,
                            },
                            '&:not(:hover) .enable-supplier-button': {
                                visibility: 'hidden',
                            },
                        }}
                        data-testclass="supplier-status-row"
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                flex: 1,
                                opacity:
                                    activationStatus ===
                                    VendorActivationStatus.VERIFIED
                                        ? 1
                                        : 0.4,
                            }}
                        >
                            <SupplierLogo
                                vendor={displayVendor}
                                title={proposal.seller.name}
                                size={26}
                            />
                            <Typography
                                variant="subtitle2"
                                data-testclass="supplier-status-name"
                            >
                                {proposal.seller.name}
                            </Typography>
                        </Box>

                        <Box sx={{ flexShrink: 0 }}>
                            {showEnableButton && (
                                <DynamicTooltip
                                    content={
                                        <Typography variant="caption">
                                            {t('Enable product ordering')}
                                        </Typography>
                                    }
                                >
                                    <Button
                                        className="enable-supplier-button"
                                        variant="contained"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEnableProductOrdering(
                                                proposal,
                                            );
                                        }}
                                        sx={{
                                            fontSize: '1.2rem',
                                        }}
                                        data-testclass="btn-enable-supplier"
                                    >
                                        {t('Enable')}
                                    </Button>
                                </DynamicTooltip>
                            )}

                            {activationStatus && (
                                <SupplierStatusChip status={activationStatus} />
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
